import { getBaseUrl } from '@/_utils/getBaseUrl.server';
import {
	AgentActivity,
	EFeedback,
	IApiResponse,
	IConversation,
	IDataPoolEntry,
	IMessage,
	IMessageDetails,
	InterruptAnswer,
	IPaginatedConversations,
	StreamEvent,
	TokenUsage
} from '@/types';
import { DEFAULT_CONVERSATION_PAGE_SIZE } from '@/_constants/defaultConversationPageSize';
import { ValidatorService } from './validator.service';

const INVALID_CONVERSATION_ID_ERROR = 'Invalid conversation id';

enum EHttpMethod {
	GET = 'GET',
	POST = 'POST',
	PUT = 'PUT',
	DELETE = 'DELETE',
	PATCH = 'PATCH'
}

export class ApiService {
	private static async fetchRaw({
		path,
		queryParams,
		method,
		data,
		keepalive,
		signal
	}: {
		path: string;
		queryParams?: URLSearchParams;
		method: EHttpMethod;
		data?: Record<string, unknown>;
		/** Ensures request completes even if user navigates away. Used by StopGeneration only. */
		keepalive?: boolean;
		/** AbortSignal for cancelling in-flight requests. Never combine with keepalive — they conflict. */
		signal?: AbortSignal;
	}): Promise<Response> {
		const baseURL = await getBaseUrl();

		const url = new URL(`${baseURL}/${path}`);

		if (queryParams) {
			// Get all keys in the URLSearchParams
			const keys = Array.from(new Set(Array.from(queryParams.keys())));

			// For each unique key
			keys.forEach((key) => {
				// Get all values for this key
				const values = queryParams.getAll(key);

				// If there's only one value, use set
				if (values.length === 1) {
					url.searchParams.set(key, values[0]);
				}
				// If there are multiple values, use append for each
				else if (values.length > 1) {
					// First clear any existing values for this key
					url.searchParams.delete(key);
					// Then append each value
					values.forEach((value) => {
						url.searchParams.append(key, value);
					});
				}
			});
		}

		return fetch(url, {
			body: data ? JSON.stringify(data) : undefined,
			headers: {
				'Content-Type': 'application/json'
			},
			method,
			credentials: 'include',
			keepalive,
			signal
		});
	}

	private static async fetch<T>(options: Parameters<typeof ApiService.fetchRaw>[0]): Promise<IApiResponse<T>> {
		const response = await this.fetchRaw(options);

		if (!response.ok) {
			// Try to parse structured error (server returns IApiResponse for known errors).
			// If parsing fails (e.g. 502 HTML page), throw like fetchSSE does.
			try {
				return (await response.json()) as IApiResponse<T>;
			} catch {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
		}

		return (await response.json()) as IApiResponse<T>;
	}

	private static async *fetchSSE(
		options: Parameters<typeof ApiService.fetchRaw>[0]
	): AsyncGenerator<StreamEvent, void, unknown> {
		const response = await this.fetchRaw(options);

		if (!response.ok) {
			// Try to extract structured error message (mirrors this.fetch() pattern)
			let errorMessage = `HTTP error! status: ${response.status}`;
			try {
				const errorBody = (await response.json()) as IApiResponse<null>;
				if (errorBody.error?.message) errorMessage = errorBody.error.message;
			} catch {
				// Response body wasn't JSON (e.g. HTML 502 page) — use generic message
			}
			throw new Error(errorMessage);
		}

		yield* this.parseSSEStream(response);
	}

	/**
	 * Create a new conversation and stream AI response in a single SSE connection.
	 * Used for optimistic conversation creation (client navigates immediately).
	 */
	static async *CreateConversationAndStream({
		conversationId,
		prompt,
		signal
	}: {
		conversationId: string;
		prompt: string;
		signal?: AbortSignal;
	}): AsyncGenerator<StreamEvent, void, unknown> {
		if (!ValidatorService.isValidId(conversationId)) {
			throw new Error(INVALID_CONVERSATION_ID_ERROR);
		}

		if (!ValidatorService.isValidPrompt(prompt)) {
			throw new Error('Invalid prompt');
		}

		yield* this.fetchSSE({
			path: '/conversations/stream',
			method: EHttpMethod.POST,
			data: { conversationId, prompt },
			signal
		});
	}

	static async GetConversations({
		page = 1,
		pageSize = DEFAULT_CONVERSATION_PAGE_SIZE
	}: { page?: number; pageSize?: number } = {}) {
		const PATH = '/conversations';
		const queryParams = new URLSearchParams({
			page: String(page),
			pageSize: String(pageSize)
		});

		return this.fetch<IPaginatedConversations>({ path: PATH, queryParams, method: EHttpMethod.GET });
	}

	static async GetConversationById(id: string) {
		if (!ValidatorService.isValidId(id)) {
			throw new Error(INVALID_CONVERSATION_ID_ERROR);
		}

		const PATH = `/conversations/${id}`;

		return this.fetch<IConversation>({ path: PATH, method: EHttpMethod.GET });
	}

	/**
	 * Delete a conversation
	 */
	static async DeleteConversation(id: string) {
		if (!ValidatorService.isValidId(id)) {
			throw new Error(INVALID_CONVERSATION_ID_ERROR);
		}

		const PATH = `/conversations/${id}`;

		return this.fetch<null>({ path: PATH, method: EHttpMethod.DELETE });
	}

	/**
	 * Update conversation visibility (public/private)
	 */
	static async UpdateConversationVisibility(id: string, isPublic: boolean) {
		if (!ValidatorService.isValidId(id)) {
			throw new Error(INVALID_CONVERSATION_ID_ERROR);
		}

		const PATH = `/conversations/${id}/visibility`;

		return this.fetch<IConversation>({ path: PATH, method: EHttpMethod.PATCH, data: { isPublic } });
	}

	/**
	 * Get a public conversation by ID (no authentication required)
	 */
	static async GetPublicConversation(id: string) {
		if (!ValidatorService.isValidId(id)) {
			throw new Error(INVALID_CONVERSATION_ID_ERROR);
		}

		const PATH = `/share/conversation/${id}`;

		return this.fetch<IConversation>({ path: PATH, method: EHttpMethod.GET });
	}

	/**
	 * Get messages for a public conversation (no authentication required)
	 */
	static async GetPublicConversationMessages(id: string) {
		if (!ValidatorService.isValidId(id)) {
			throw new Error(INVALID_CONVERSATION_ID_ERROR);
		}

		const PATH = `/share/conversation/${id}/messages`;

		return this.fetch<IMessage[]>({ path: PATH, method: EHttpMethod.GET });
	}

	/**
	 * Get heavy message details for lazy loading (activities, dataPoolEntries, dbCitations, tokenUsage)
	 */
	static async GetMessageDetails(messageId: string) {
		if (!ValidatorService.isValidId(messageId)) {
			throw new Error('Invalid message id');
		}

		const PATH = `/messages/${messageId}/details`;

		return this.fetch<IMessageDetails>({ path: PATH, method: EHttpMethod.GET });
	}

	/**
	 * Parse SSE stream into StreamEvent objects
	 */
	private static async *parseSSEStream(response: Response): AsyncGenerator<StreamEvent, void, unknown> {
		if (!response.body) {
			throw new Error('Response body is null');
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = '';

		try {
			// Sequential reading is required for streams
			while (true) {
				const { done, value } = await reader.read();

				if (done) {
					break;
				}

				buffer += decoder.decode(value, { stream: true });

				// Split by double newline (SSE format)
				const lines = buffer.split('\n\n');

				// Keep the last incomplete line in the buffer
				buffer = lines.pop() || '';

				for (const line of lines) {
					if (line.startsWith('data: ')) {
						const jsonStr = line.slice(6); // Remove 'data: ' prefix
						try {
							const event = JSON.parse(jsonStr) as StreamEvent;
							yield event;
						} catch (e) {
							console.error('Failed to parse SSE event:', e, jsonStr);
						}
					}
				}
			}
		} finally {
			reader.releaseLock();
		}
	}

	/**
	 * Get messages with streaming - always expects SSE format
	 */
	static async *GetMessagesWithStreaming({
		conversationId,
		signal
	}: {
		conversationId: string;
		signal?: AbortSignal;
	}): AsyncGenerator<StreamEvent, void, unknown> {
		if (!ValidatorService.isValidId(conversationId)) {
			throw new Error(INVALID_CONVERSATION_ID_ERROR);
		}

		yield* this.fetchSSE({ path: `/conversations/${conversationId}/messages`, method: EHttpMethod.GET, signal });
	}

	/**
	 * Generate AI response without loading existing messages (performance optimization)
	 * Use this when messages are already cached on the client
	 */
	static async *GenerateAIResponse({
		conversationId,
		signal
	}: {
		conversationId: string;
		signal?: AbortSignal;
	}): AsyncGenerator<StreamEvent, void, unknown> {
		if (!ValidatorService.isValidId(conversationId)) {
			throw new Error(INVALID_CONVERSATION_ID_ERROR);
		}

		const queryParams = new URLSearchParams({ skipMessages: 'true' });
		yield* this.fetchSSE({
			path: `/conversations/${conversationId}/messages`,
			method: EHttpMethod.GET,
			queryParams,
			signal
		});
	}

	/**
	 * Stream reply for message with Server-Sent Events
	 */
	static async *StreamReplyForMessage({
		conversationId,
		prompt,
		signal
	}: {
		conversationId: string;
		prompt: string;
		signal?: AbortSignal;
	}): AsyncGenerator<StreamEvent, void, unknown> {
		if (!ValidatorService.isValidId(conversationId)) {
			throw new Error(INVALID_CONVERSATION_ID_ERROR);
		}

		if (!ValidatorService.isValidPrompt(prompt)) {
			throw new Error('Invalid prompt');
		}

		yield* this.fetchSSE({
			path: `/conversations/${conversationId}/messages`,
			method: EHttpMethod.POST,
			data: { prompt },
			signal
		});
	}

	/**
	 * Edit a user message and stream the new AI response via SSE
	 */
	static async *EditMessage({
		conversationId,
		messageId,
		text,
		signal
	}: {
		conversationId: string;
		messageId: string;
		text: string;
		signal?: AbortSignal;
	}): AsyncGenerator<StreamEvent, void, unknown> {
		if (!ValidatorService.isValidId(conversationId)) throw new Error(INVALID_CONVERSATION_ID_ERROR);
		if (!ValidatorService.isValidId(messageId)) throw new Error('Invalid message id');

		yield* this.fetchSSE({
			path: `/conversations/${conversationId}/messages/${messageId}`,
			method: EHttpMethod.PATCH,
			data: { text },
			signal
		});
	}

	/**
	 * Regenerate an AI response for an assistant message via SSE
	 */
	static async *RegenerateMessage({
		conversationId,
		messageId,
		signal
	}: {
		conversationId: string;
		messageId: string;
		signal?: AbortSignal;
	}): AsyncGenerator<StreamEvent, void, unknown> {
		if (!ValidatorService.isValidId(conversationId)) throw new Error(INVALID_CONVERSATION_ID_ERROR);
		if (!ValidatorService.isValidId(messageId)) throw new Error('Invalid message id');

		yield* this.fetchSSE({
			path: `/conversations/${conversationId}/messages/${messageId}/regenerate`,
			method: EHttpMethod.POST,
			signal
		});
	}

	/**
	 * Resume an interrupted AI graph with the user's answer.
	 * The messageId is the original user message ID (thread_id for the checkpoint).
	 */
	static async *ResumeInterrupt({
		conversationId,
		messageId,
		answer,
		signal
	}: {
		conversationId: string;
		messageId: string;
		answer: InterruptAnswer;
		signal?: AbortSignal;
	}): AsyncGenerator<StreamEvent, void, unknown> {
		if (!ValidatorService.isValidId(conversationId)) throw new Error(INVALID_CONVERSATION_ID_ERROR);
		if (!ValidatorService.isValidId(messageId)) throw new Error('Invalid message id');

		yield* this.fetchSSE({
			path: `/conversations/${conversationId}/messages/${messageId}/resume`,
			method: EHttpMethod.POST,
			data: { answer },
			signal
		});
	}

	/**
	 * Stop an in-progress AI response generation.
	 * Persists the partial state and releases the request lock.
	 */
	static async StopGeneration({
		conversationId,
		partialText,
		activities,
		tokenUsage
	}: {
		conversationId: string;
		partialText: string;
		activities: AgentActivity[];
		tokenUsage: TokenUsage | null;
	}) {
		if (!ValidatorService.isValidId(conversationId)) {
			throw new Error(INVALID_CONVERSATION_ID_ERROR);
		}

		const PATH = `/conversations/${conversationId}/messages/stop`;

		return this.fetch<IMessage[]>({
			path: PATH,
			method: EHttpMethod.POST,
			data: { partialText, activities, tokenUsage },
			keepalive: true // Ensure request completes even if user navigates away / refreshes
		});
	}

	/**
	 * Update message feedback (thumbs up/down)
	 */
	static async UpdateMessageFeedback({
		conversationId,
		messageId,
		feedback
	}: {
		conversationId: string;
		messageId: string;
		feedback: EFeedback | null;
	}) {
		if (!ValidatorService.isValidId(conversationId)) {
			throw new Error(INVALID_CONVERSATION_ID_ERROR);
		}

		if (!ValidatorService.isValidId(messageId)) {
			throw new Error('Invalid message id');
		}

		const PATH = `/conversations/${conversationId}/messages/${messageId}/feedback`;

		return this.fetch<IMessage>({ path: PATH, method: EHttpMethod.PATCH, data: { feedback } });
	}

	/**
	 * Get data pool entries for a conversation
	 */
	static async GetDataPool(conversationId: string, source?: string) {
		if (!ValidatorService.isValidId(conversationId)) {
			throw new Error(INVALID_CONVERSATION_ID_ERROR);
		}

		const PATH = `/conversations/${conversationId}/data-pool`;
		const queryParams = source ? new URLSearchParams({ source }) : undefined;

		return this.fetch<IDataPoolEntry[]>({ path: PATH, queryParams, method: EHttpMethod.GET });
	}

	/**
	 * Request password reset email
	 * Note: Uses raw fetch since better-auth endpoints are at /api/auth (not /api/v1)
	 */
	static async RequestPasswordReset(email: string, redirectTo: string): Promise<{ success: boolean; error?: string }> {
		try {
			const response = await fetch('/api/auth/request-password-reset', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email, redirectTo }),
				credentials: 'include'
			});

			if (!response.ok) {
				const data = await response.json().catch(() => ({}));
				return { success: false, error: data.message || 'Failed to send reset email' };
			}

			return { success: true };
		} catch (error) {
			console.error('[ApiService] RequestPasswordReset error:', error);
			return { success: false, error: 'Network error. Please try again.' };
		}
	}
}
